import {
  type Fare,
  type FareDetailsV2,
  type FarePriceBreakdown,
  type FareSliceDeparture,
  type FareSliceDetailsV2,
  type FareSliceOutbound,
  type FixedPointsEligibilityStatus,
  FixedPointsEligibilityStatusEnum,
  type Flights,
  IneligibleEnum,
  type MulticityFlights,
  type Prices,
} from "@b2bportal/air-shopping-api";
import { getFareSliceId } from "@b2bportal/core-flights";
import { FlightRatingsEnum, type HtsAirCfarOffer } from "@b2bportal/core-types";
import {
  addPrices,
  compareFaresByPrice,
  getFareSliceRating,
  getFilteredFares,
} from "@b2bportal/core-utilities";
import type { IFareData, IFlightFares } from "@overrides/types";
import { isEmpty } from "lodash-es";

/**
 * RBC-specific fare class filter logic that fixes RBC-1170.
 *
 * Unlike the core implementation, this only shows fares that match the selected
 * fare class filters, without showing all higher-tier fares.
 *
 * RBC transforms "enhanced" (rating 2) to "standard" (rating 1), so when filtering
 * by Standard, we need to include both Standard and Enhanced fare classes.
 */
export const filterWithFareShelfRatings = (
  selectedFareClassFilters: Array<number>,
  fareSliceRating: number
) => {
  // Transform enhanced (2) to standard (1) to match RBC's 4-fare system
  const normalizedRating =
    fareSliceRating === FlightRatingsEnum.enhanced
      ? FlightRatingsEnum.standard
      : fareSliceRating;

  return selectedFareClassFilters.includes(normalizedRating);
};

export const createSyntheticFareData = (
  fares: IFlightFares,
  syntheticFaresOffersData?: Record<string, HtsAirCfarOffer[]>
) => {
  return Object.entries(fares).reduce((acc, [fareClass, fare]) => {
    const cfarOffers = syntheticFaresOffersData?.[fare.fareId];
    const hasCfarOffer = cfarOffers && cfarOffers.length > 0;

    if (hasCfarOffer) {
      const cfarOffer = cfarOffers[0];

      let syntheticPrice: Prices | FarePriceBreakdown;

      if ("fiat" in fare.price) {
        syntheticPrice = addPrices(fare.price, cfarOffer.feeAmountPerPassenger);
      } else {
        syntheticPrice = {
          baseAmount: fare.price.baseAmount,
          taxesAndFees: addPrices(
            fare.price.taxesAndFees,
            cfarOffer.feeAmountPerPassenger
          ),
          taxLineItems: fare.price.taxLineItems,
        };
      }

      acc[fareClass] = {
        ...fare,
        price: syntheticPrice,
      };
    }

    return acc;
  }, {} as IFlightFares);
};
export const getCheapestFilteredFare = (
  fares: IFlightFares,
  fareShelfFilter: Array<number>,
  shouldDisplaySyntheticFares?: boolean,
  syntheticFaresOffersData?: Record<string, HtsAirCfarOffer[]>
): IFareData => {
  const filteredFares = getFilteredFares(fares, fareShelfFilter);

  const filteredSyntheticFares = createSyntheticFareData(
    filteredFares,
    syntheticFaresOffersData
  );

  const getPriceValue = (price: Prices | FarePriceBreakdown): number => {
    return "fiat" in price
      ? price.fiat.value
      : "taxesAndFees" in price
        ? price.taxesAndFees.fiat.value
        : Number.POSITIVE_INFINITY;
  };

  const faresToCompare =
    shouldDisplaySyntheticFares && !isEmpty(filteredSyntheticFares)
      ? filteredSyntheticFares
      : filteredFares;

  const fareValues = Object.values(faresToCompare);

  return fareValues
    .slice(1)
    .reduce(
      (cheapest, fare) =>
        getPriceValue(fare.price) < getPriceValue(cheapest.price)
          ? fare
          : cheapest,
      fareValues[0]
    );
};

/**
 * Of two fares in one fare class group, the fare the group is advertised at on
 * Grid V2: the lower points band wins, then the lower taxes. When either fare
 * has no points for the account (flex, or a sub-minimum fare shown as flex)
 * both are compared on the cash the client pays (taxes for a fixed fare, the
 * total for a flex one), like today's cheapest-fare rule; the sub-minimum
 * presentation itself is RBC-2582. `getFareData` folds a group through this
 * once the V2 card is wired (RBC-2599).
 */
export const gridV2LowestBandFare = (
  a: IFareData,
  b: IFareData,
  rewardsAccountId: string
): IFareData => {
  const points = (fare: IFareData): number | undefined =>
    "baseAmount" in fare.price
      ? fare.price.baseAmount.accountSpecific[rewardsAccountId]?.value
      : undefined;
  const cash = (fare: IFareData): number =>
    "baseAmount" in fare.price
      ? fare.price.taxesAndFees.fiat.value
      : fare.price.fiat.value;

  const pointsA = points(a);
  const pointsB = points(b);
  if (pointsA != null && pointsB != null && pointsA !== pointsB) {
    return pointsA < pointsB ? a : b;
  }
  return cash(b) < cash(a) ? b : a;
};

/**
 * The carrier surcharges (YQ/YR) a fare's fixed-points price covers, per
 * passenger. The shop contract puts it on every eligibility case except
 * `BelowMinimumBand` (b2b-interfaces #2126), so the card reads it through here.
 */
export const gridV2CoveredSurcharges = (
  status: FixedPointsEligibilityStatus | undefined
): Prices | undefined => {
  if (status == null) return undefined;
  if (
    status.FixedPointsEligibilityStatus ===
      FixedPointsEligibilityStatusEnum.Ineligible &&
    status.Ineligible === IneligibleEnum.BelowMinimumBand
  ) {
    return undefined;
  }
  return status.coveredSurcharges;
};

export const getFilteredFareDetails = ({
  fareDetails,
  selectedFareClassFilters,
  sliceIndex,
  selectedFareId,
}: {
  fareDetails: FareDetailsV2[];
  selectedFareClassFilters: Array<number>;
  sliceIndex: number;
  selectedFareId?: string;
}): FareDetailsV2[] => {
  // userSelectedFare, defaults to first fare in list if none selected
  const selectedFare = fareDetails.find((f) => f.id === selectedFareId);

  const selectedFareRating =
    selectedFare?.slices[sliceIndex]?.fareShelf?.rating || -1;
  return fareDetails
    .filter((fare: FareDetailsV2) => {
      const fareSlice = fare.slices[sliceIndex];
      const fareSliceRating = getFareSliceRating(fareSlice);
      // Validate that fare slice has fareShelf defined
      if (fareSlice.fareShelf) {
        if (selectedFareClassFilters.length > 0) {
          // Filters applied
          return filterWithFareShelfRatings(
            selectedFareClassFilters,
            fareSliceRating
          );
        } else {
          // No filters applied
          if (selectedFareRating >= 0) {
            // If there's a selectedFare, show higher fares only
            return fareSliceRating >= selectedFareRating;
          }
        }
      }
      return true;
    })
    .sort(compareFaresByPrice);
};

export const getFarePrice = (
  fare: Fare | FareSliceOutbound | FareSliceDeparture
) =>
  fare.amount?.fiat?.value ??
  fare.priceBreakdown?.taxesAndFees?.fiat?.value ??
  Infinity;

// For fixed pricing, we only show the lowest two fares between basic, standard and enhanced fare classes
export const filterLowestFares = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  fares: T[],
  flights: Flights | MulticityFlights
): T[] => {
  const faresWithShelf = fares.map((fare) => {
    const fareSliceId = getFareSliceId(fare);
    const fareShelf = flights.fareSlices[fareSliceId].fareShelf.value;
    return { fare, fareShelf };
  });

  const highShelfFares = faresWithShelf
    .filter(({ fareShelf }) => fareShelf >= 3)
    .map(({ fare }) => fare);

  const lowestTwoLowShelfFares = faresWithShelf
    .filter(({ fareShelf }) => fareShelf <= 2)
    .sort((a, b) => getFarePrice(a.fare) - getFarePrice(b.fare))
    .slice(0, 2)
    .map(({ fare }) => fare);

  return [...highShelfFares, ...lowestTwoLowShelfFares];
};

// Business decision: only surface scarcity at 5 seats or fewer.
export const SEATS_REMAINING_THRESHOLD = 5;

// A slice can only be sold up to its most constrained segment, hence the min.
// Absent counts are skipped, but a zero is kept so one sold-out segment sinks the
// whole slice rather than advertising the other legs' seats.
export const getSeatsRemaining = (
  fareSlice: FareSliceDetailsV2
): number | undefined => {
  const seatCounts = (fareSlice?.fareDetails?.segments ?? [])
    .map((segment) => segment?.availableSeats)
    .filter((seats): seats is number => typeof seats === "number");

  return seatCounts.length > 0 ? Math.min(...seatCounts) : undefined;
};

export const shouldShowSeatsRemaining = (
  seatsRemaining: number | undefined,
  threshold: number = SEATS_REMAINING_THRESHOLD
): seatsRemaining is number =>
  seatsRemaining != null && seatsRemaining > 0 && seatsRemaining <= threshold;
